import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dog, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid or missing reset token.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success('Password has been reset. You can now sign in.');
      navigate('/auth');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired reset token.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 animate-fade-in" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#4A90E2] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Dog className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-['Nunito'] font-extrabold text-2xl text-[#1A1D20] tracking-tight">Choose New Password</h1>
          <p className="text-sm text-[#5C636A] mt-1">Enter a new password for your DoggoHub account</p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-xs font-medium text-[#5C636A]">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 rounded-xl bg-gray-50"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              className="w-full h-12 rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
              data-testid="reset-password-submit-button"
            >
              {loading ? 'Please wait...' : 'Reset Password'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}